// DQ-8766: Source-aware login redirect after common signup.
//
// The `source` query param on the common signup URL carries the full URL the
// caller wants to return to after a successful registration. Any origin is
// accepted, so the feature works unchanged in every deployed environment
// without a hardcoded domain or per-environment configuration.
//
// SECURITY: the source URL is client-supplied, so it is never navigated to
// as-is. It is parsed with the URL API and only accepted when it is an http(s)
// URL that carries no embedded credentials. Anything else — a non-navigation
// scheme, a malformed URL, a bare token or a non-root-relative path — falls
// back to this application's own login page. No referrer or storage value is
// ever used.

// Query param carrying the return URL on the common signup URL
export const SIGNUP_SOURCE_PARAM = "source";

// Login path of this application, used to build the fallback destination
export const LOGIN_PATH = "/login";

// Current browser origin, empty when unavailable (e.g. non-browser environment)
export const getCurrentOrigin = (): string =>
  typeof window !== "undefined" && window.location
    ? window.location.origin
    : "";

// This application's own login URL on the given origin
export const buildLoginUrl = (currentOrigin: string = getCurrentOrigin()) =>
  `${currentOrigin}${LOGIN_PATH}`;

// Signup path used by DQ navigation, carrying DQ's own login URL as the source
export const buildSignupPath = (currentOrigin: string = getCurrentOrigin()) =>
  `/signup?${SIGNUP_SOURCE_PARAM}=${encodeURIComponent(
    buildLoginUrl(currentOrigin),
  )}`;

// Resolves the post-signup destination from the client-supplied source URL,
// falling back to this application's login page for anything unsafe
export const resolveSignupRedirectUrl = (
  source: string | null | undefined,
  currentOrigin: string = getCurrentOrigin(),
): string => {
  const fallback = buildLoginUrl(currentOrigin);
  if (typeof source !== "string") return fallback;

  const candidate = source.trim();
  if (!candidate) return fallback;

  // A relative source is only honored as a root-relative path, so bare tokens
  // (e.g. a legacy `source=dq`) and traversal attempts are rejected instead of
  // being resolved against the current origin.
  const isAbsolute = /^[a-z][a-z0-9+.-]*:/i.test(candidate);
  if (!isAbsolute && !candidate.startsWith("/")) return fallback;

  let url: URL;
  try {
    url = new URL(candidate, currentOrigin || undefined);
  } catch {
    return fallback;
  }

  if (url.protocol !== "http:" && url.protocol !== "https:") return fallback;
  if (url.username || url.password) return fallback;

  return url.href;
};

// Performs the full-page redirect to the resolved destination
export const redirectToLoginForSource = (
  source: string | null | undefined,
): void => {
  window.location.assign(resolveSignupRedirectUrl(source));
};
